import axios from '@/utils/request'
import Qs from 'qs'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { BaseResponse, ResponseStatus } from '.'
import {
  TournamentTeamEntity,
  TournamentTeamResponsePagination,
  TournamentTeamState,
  TournamentTeamRole
} from './tournamentTeam'
import { StageEntity } from './stage'
import { TournamentCatalogSearchFilter } from '@/modules/tournament/tournament.store'
import { TournamentTeamPostEntity } from '@/api/tournamentTeamPost'
import { VenueEntity } from '@/api/venue'
import { RegionInfo, SupportedGame } from '@/api/config'

export { BaseResponse, ResponseStatus }

export interface TournamentEntityPagination {
  items: TournamentEntity[]
  hasMorePage: boolean
}

export interface Money {
  currency: string
  amount: string
}

export interface TournamentEntity {
  tournamentId: string
  name: string
  game: string
  regions: RegionInfo[]
  isPublished: boolean
  isFeatured: boolean
  tournamentCheckInDuration: number | null
  isTournamentCheckInEnable: boolean
  tournamentCheckInEndTime: number | null
  createTime: number
  publishTime: number
  scheduleRegistrationStartTime: number
  scheduleRegistrationEndTime: number
  scheduleStartTime: number
  scheduleEndTime: number
  detailIsVisible: boolean
  detailBanner: string
  detailMobileBanner: string
  detailLogo: string
  detailAbout: string
  detailRules: string
  capacityMinTeams: number
  capacityMaxTeams: number
  capacityMinPlayersPerTeam: number
  capacityMaxPlayersPerTeam: number
  prizePrizePool: string
  prizeChampionPrize: string
  prizeFirstRunnerUpPrize: string
  prizeSecondRunnerUpPrize: string
  prizePrizeDetails: string
  prizeIsPrizeFreeText: boolean
  paymentPerTeamRegistrationFee: number
  state: TournamentState
  stages: StageEntity[]
  isDiscordIdRequired: boolean
  isAutomated: boolean
  totalTeamNum: number
  totalRegisteredTeamNum: number
  hasEntryFee: boolean
  entryFee: Money | null
  hasGroup: boolean
}

export interface TournamentVenueWithDetail {
  venue: VenueEntity
  current: number
  quota: number | null
}

export interface MemberJoinedTournamentsEntity {
  tournamentId: string
  tournamentTeamId: string
  name: string
  game: string
  regions: RegionInfo[]
  banner: string
  logo: string
  scheduleStartTime: number
  scheduleEndTime: number
  isAutomated: boolean
  state: TournamentState
  teamState: TournamentTeamState
  isTeamStarter: boolean
  isTournamentCheckedIn: boolean
  role: TournamentTeamRole
  hasUpcomingMatch: boolean
}

export enum TournamentState {
  Initial = 'Initial',
  Published = 'Published',
  OpenRegistration = 'OpenRegistration',
  CompleteRegistration = 'CompleteRegistration',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  WaitingForCheckIn = 'WaitingForCheckIn'
}

export interface TeamTokenInfo {
  tournamentId: string
  tournamentTeamId: string
  teamName: string
  teamLogo: string
}

export interface CreateTournamentTeamRequest {
  name: string
  logo: string | null
  group: string | null
}

export interface ConfirmTournamentTeamRosterRequest {
  memberIds: string[]
  paymentMethod: string | undefined
}

export interface MemberTournamentFormResponseEntity {
  createTime: number
  updateTime: number
  schema: string
  tournamentId: string
  tournamentTeamId: string
  value: string
}

export enum TournamentGameType {
  All = '',
  Lol = 'lol',
  Raingbow6 = 'rainbow6',
  Csgo = 'csgo',
  Valorant = 'valorant',
  WildRift = 'wildRift',
  Aov = 'aov',
  SlamDunk = 'slamDunk',
  Fifa21 = 'fifa21',
  Forts = 'forts'
}

export interface MemberTournamentHistoryItemEntity {
  tournament: TournamentEntity
  tournamentTeam: TournamentTeamEntity
  stageName: string
  groupName: string
  standing: string
}

export interface MemberTournamentHistoryPaginatedResponse {
  items: MemberTournamentHistoryItemEntity[]
  hasMorePage: boolean
}

export interface TournamentCustomFormEntity {
  schema: string
}

export interface MemberJoinedAutomatedTournamentResponse {
  isJoined: boolean
  tournamentId: string
  state: TournamentState
  isWaitingForUserToCheckIn: boolean
  tournamentCheckInEndTime: number
  tournamentCheckInRemainingMinutes: number
}

export enum ConfirmTournamentTeamRosterStatus {
  Success = 'Success',
  Delayed = 'Delayed',
  PaymentMethodRequired = 'PaymentMethodRequired',
  RedirectToPaymentGateway = 'RedirectToPaymentGateway'
}

export interface ConfirmTournamentTeamRosterResponse {
  status: ConfirmTournamentTeamRosterStatus
  fee: string
  walletTransactionId: string
  paymentMethod: string
  paymentMethodOrderId: string
}

export interface TournamentGroupAvailability {
  name: string
  maxTeams: number
  current: number
  available: boolean
}

export class TournamentAPI {
  public static API_PREFIX = '/tournament/v1'

  /**
   * Retrieve published tournaments in paginated manner
   * @param page Page number
   * @param all [ADMIN ONLY] List all tournaments (included hidden tournaments)
   * @returns {BaseResponse<TournamentEntityPagination>} Pagination data
   */
  public static getTournaments(
    page: number = 1,
    filter: TournamentCatalogSearchFilter | {} = {}
  ): AxiosPromise<BaseResponse<TournamentEntityPagination>> {
    return axios.get(`${this.API_PREFIX}/tournaments`, {
      params: {
        page,
        ...filter
      },
      paramsSerializer: params =>
        Qs.stringify(params, { arrayFormat: 'repeat' })
    })
  }

  /**
   * Get information of a specific tournament
   * @param tournamentId Tournament ID
   * @returns {BaseResponse<TournamentEntity>} Tournament details
   */
  public static getTournament(
    tournamentId: string
  ): AxiosPromise<BaseResponse<TournamentEntity>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}`)
  }

  /**
   * Get information of a specific stage
   * @param stageId Stage ID
   * @returns {BaseResponse<TournamentEntity>} Stage details
   */
  public static getStage(
    stageId: string
  ): AxiosPromise<BaseResponse<TournamentEntity>> {
    return axios.get(`${this.API_PREFIX}/stages/${stageId}`)
  }

  /**
   * Get tournament teams under a tournament
   * @param tournamentId Tournament ID
   * @param page Page number
   * @returns {TournamentTeamResponsePagination} Pagination data
   */
  public static getTournamentTeams(
    tournamentId: string,
    page: number = 1,
    isRegistered: boolean = false
  ): AxiosPromise<BaseResponse<TournamentTeamResponsePagination>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/teams`, {
      params: {
        page,
        isRegistered
      }
    })
  }

  /**
   * Get the tournament team that the member is joined under a tournament
   * @param memberId Member ID
   * @param tournamentId Tournament ID
   * @param isRequestOnly
   * @returns {BaseResponse<TournamentTeamEntity>} Tournament team data
   */
  public static getMemberJoinedTournamentTeam(
    memberId: string,
    tournamentId: string,
    playerRoles: TournamentTeamRole[] | null = null
  ): AxiosPromise<BaseResponse<TournamentTeamEntity>> {
    if (!playerRoles) {
      playerRoles = [
        TournamentTeamRole.Captain,
        TournamentTeamRole.Member,
        TournamentTeamRole.RequestToJoin
      ]
    }
    return axios.get(
      `${this.API_PREFIX}/members/${memberId}/tournaments/${tournamentId}/teams`,
      {
        params: {
          playerRoles: playerRoles
        },
        paramsSerializer: params =>
          Qs.stringify(params, { arrayFormat: 'repeat' })
      }
    )
  }

  /**
   * Get member joined tournaments
   * @param memberId Logged-in member ID
   * @param registeredOnly
   * @returns {BaseResponse<string[]>} Tournament IDs
   */
  public static getMemberJoinedTournaments(
    memberId: string = '@me',
    registeredOnly: boolean | undefined = undefined
  ): AxiosPromise<BaseResponse<MemberJoinedTournamentsEntity[]>> {
    return axios.get(
      `${this.API_PREFIX}/members/${memberId}/tournaments/joined-tournaments`,
      { params: { registeredOnly } }
    )
  }

  /**
   * Get team token
   * @param teamToken Team token
   * @returns {BaseResponse<TeamTokenInfo>}
   */
  public static getTeamTokenInformation(
    teamToken: string
  ): AxiosPromise<BaseResponse<TeamTokenInfo>> {
    return axios.get(`${this.API_PREFIX}/team-token/${teamToken}`)
  }

  /**
   * Get tournament stages
   * @param tournamentId Tournament ID
   * @returns {BaseResponse<StageEntity[]>} List of stages
   */
  public static getTournamentStages(
    tournamentId: string
  ): AxiosPromise<BaseResponse<StageEntity[]>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/stages`)
  }

  /**
   * Confirm tournament team roster
   * @param tournamentId Tournament ID
   * @param tournamentTeamId Tournament team ID
   * @param request {ConfirmTournamentTeamRosterRequest} Roster request payload
   * @returns {BaseResponse} API Response
   */
  public static confirmTournamentTeamRoster(
    tournamentId: string,
    tournamentTeamId: string,
    request: ConfirmTournamentTeamRosterRequest
  ): AxiosPromise<BaseResponse<ConfirmTournamentTeamRosterResponse>> {
    return axios.put(
      `${this.API_PREFIX}/tournaments/${tournamentId}/teams/${tournamentTeamId}/confirm-roster`,
      request
    )
  }

  /**
   * Join tournament team by team token / accept invitation
   * @param teamToken Team token
   * @returns {BaseResponse} API Response
   */
  public static joinTournamentTeamByTeamToken(
    teamToken: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(`${this.API_PREFIX}/team-token/${teamToken}/join-team`)
  }

  public static submitTournamentFormResponseWithToken(
    teamToken: string,
    formResponse: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(`${this.API_PREFIX}/team-token/${teamToken}/form`, {
      value: formResponse
    })
  }

  public static submitTournamentFormResponse(
    tournamentTeamId: string,
    formResponse: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/form`,
      {
        value: formResponse
      }
    )
  }

  public static updateTournamentFormResponse(
    tournamentTeamId: string,
    formResponse: string
  ): AxiosPromise<BaseResponse> {
    return axios.put(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/form`,
      {
        value: formResponse
      }
    )
  }

  public static getMemberTournamentFormResponse(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse<MemberTournamentFormResponseEntity>> {
    return axios.get(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/form`
    )
  }

  /**
   * Regenerate team token
   * @param tournamentTeamId Tournament team ID
   * @returns {BaseResponse<string>} New team token
   */
  public static refreshTeamToken(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse<string>> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/refresh-token`
    )
  }

  /**
   * Remove team member from tournament team
   * @param teamId
   * @param memberId The member id to be removed
   */
  public static removeTeamMember(
    teamId: string,
    memberId: string
  ): AxiosPromise<BaseResponse> {
    return axios.delete(
      `${this.API_PREFIX}/tournament-teams/${teamId}/members/${memberId}`
    )
  }

  /**
   * Create tournament team
   * @param tournamentId Tournament ID
   * @param createTournamentTeamRequest Team Info Payload
   * @param logoFile
   * @returns {BaseResponse<string>} API Response
   */
  public static createTournamentTeam(
    tournamentId: string,
    createTournamentTeamRequest: CreateTournamentTeamRequest,
    logoFile: File | Blob | null = null
  ): AxiosPromise<BaseResponse<string>> {
    const request = new FormData()
    request.append('json', JSON.stringify(createTournamentTeamRequest))

    if (logoFile) {
      request.append('image', logoFile)
    }

    return axios.post(
      `${this.API_PREFIX}/tournaments/${tournamentId}/teams`,
      request
    )
  }

  /**
   * Get member tournament history
   * @param memberId Member ID
   * @param page Page number
   * @returns {BaseResponse} API Response
   */
  public static getMemberTournamentHistory(
    memberId: string,
    page: number = 1,
    gameType: string = ''
  ): AxiosPromise<BaseResponse<MemberTournamentHistoryPaginatedResponse>> {
    return axios.get(`/tournament/v1/members/${memberId}/history`, {
      params: {
        page,
        gameType
      }
    })
  }

  /**
   * Get tournament team
   * @param tournamentTeamId Tournament Team ID
   * @returns {BaseResponse<TournamentTeamEntity>} API Response
   */
  public static getTournamentTeam(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse<TournamentTeamEntity>> {
    return axios.get(`${this.API_PREFIX}/tournament-teams/${tournamentTeamId}`)
  }

  /**
   * Get tournament venues
   * @param tournamentId Tournament Id
   * @returns {BaseResponse} API Response
   */
  public static getTournamentVenues(
    tournamentId: string
  ): AxiosPromise<BaseResponse<TournamentVenueWithDetail[]>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/venues`)
  }

  /**
   * Leave tournament team aka self kicking (LeaveFromTournamentTeam)
   * @param tournamentTeamId Tournament Team ID
   * @returns {BaseResponse} API Response
   */
  public static leaveFromTournamentTeam(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse> {
    return axios.delete(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/members`
    )
  }

  /**
   * Change tournament team captain
   * @param tournamentTeamId Tournament Team ID
   * @param request Change tournament team captain request
   * @returns {BaseResponse} API Response
   */
  public static changeTournamentTeamCaptain(
    tournamentTeamId: string,
    newCaptainMemberId: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/change-captain`,
      {
        newCaptainMemberId: newCaptainMemberId
      }
    )
  }

  /**
   * Change tournament team's game account of a player
   * @param tournamentTeamId Tournament Team ID
   * @param name the new account name
   * @returns {BaseResponse} API Response
   */
  public static changeTournamentTeamGameAccount(
    tournamentTeamId: string,
    accountName: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/game-accounts`,
      {
        name: accountName
      }
    )
  }

  /**
   * Undo confirm tournament team roster
   * @param tournamentTeamId Tournament Team ID
   * @returns {BaseResponse} API Response
   */
  public static undoTournamentTeamRosterConfirmation(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/unconfirm-roster`
    )
  }

  /**
   * Get tournament team count
   * @param tournamentId Tournament ID
   * @returns {BaseResponse} API Response
   */
  public static getRegisteredTeamCount(
    tournamentId: string
  ): AxiosPromise<BaseResponse<number>> {
    return axios.get(
      `${this.API_PREFIX}/tournaments/${tournamentId}/team-count`
    )
  }

  public static tournamentCheckIn(
    tournamentTeamId: string
  ): AxiosPromise<BaseResponse<number>> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}/check-in`
    )
  }

  public static getTournamentCustomFormSchema(
    tournamentId: string
  ): AxiosPromise<BaseResponse<TournamentCustomFormEntity>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/form`)
  }

  public static getAutomatedTournamentGameList(
    isPast: boolean = false,
    region: string | undefined = undefined
  ): AxiosPromise<BaseResponse<SupportedGame[]>> {
    return axios.get(`${this.API_PREFIX}/automated-tournament/games`, {
      params: {
        past: isPast,
        region
      }
    })
  }

  public static getAutomatedTournamentCatalog(
    page: number,
    game: string,
    regions: string[] | undefined
  ): AxiosPromise<BaseResponse<TournamentEntityPagination>> {
    return axios.get(`${this.API_PREFIX}/automated-tournament/catalog`, {
      params: {
        page,
        game,
        regions
      },
      paramsSerializer: params =>
        Qs.stringify(params, { arrayFormat: 'repeat' })
    })
  }

  public static getMemberJoinedAutomatedTournament(): AxiosPromise<
    BaseResponse<MemberJoinedAutomatedTournamentResponse>
  > {
    return axios.get(`${this.API_PREFIX}/automated-tournament/joined`)
  }

  public static getInstantMatchPastTournaments(
    page: number = 1,
    filter: TournamentCatalogSearchFilter | {} = {}
  ): AxiosPromise<BaseResponse<TournamentEntityPagination>> {
    return axios.get(`${this.API_PREFIX}/automated-tournament/past`, {
      params: {
        page,
        ...filter
      },
      paramsSerializer: params =>
        Qs.stringify(params, { arrayFormat: 'repeat' })
    })
  }

  public static getTournamentGroups(
    tournamentId: string
  ): AxiosPromise<BaseResponse<TournamentGroupAvailability[]>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/groups`)
  }

  public static editTournamentTeam(
    tournamentTeamId: string,
    request: {
      group: string
    }
  ): AxiosPromise<BaseResponse<number>> {
    return axios.post(
      `${this.API_PREFIX}/tournament-teams/${tournamentTeamId}`,
      request
    )
  }

  // FIXME: temp code
  public static shouldHideForm(): AxiosPromise<BaseResponse<boolean>> {
    return axios.get(`${this.API_PREFIX}/members/@me/hide-form`)
  }
  // end of temp code

  public static uploadUserGeneratedContent(
    tournamentId: string,
    file: FormData,
    axiosConfig?: AxiosRequestConfig
  ): AxiosPromise<BaseResponse<string>> {
    return axios.post(
      `${this.API_PREFIX}/tournaments/${tournamentId}/usercontent`,
      file,
      axiosConfig
    )
  }
}
